/**
 * Data Signal Marketplace — browse / discover.
 *
 * Two stages, URL-driven so every view is shareable:
 *
 *   1. HOME    — large hero search + main category tiles (the only
 *                place we use the category brand colors / gradients).
 *                URL: /marketplace
 *
 *   2. BROWSE  — clean header (no colors), the SAME compact search
 *                bar that's shown everywhere from this point on,
 *                a horizontal strip of refined sub-category pills
 *                under the search, then a real DataTable of
 *                listings (same component used on the Trainings page).
 *                Default sort is "Most subscribed first".
 *                URL: /marketplace?cat=<root>[&sub=<slug>][&q=<text>]
 *
 * Clicking a listing row goes straight to ``/marketplace/listings/:id``.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  Avatar, Badge, Box, Button, Card, Chip,
  Divider, FormControl, FormControlLabel, IconButton,
  InputAdornment, MenuItem, Paper, Popover, Select, Stack,
  Switch, TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import VerifiedIcon from '@mui/icons-material/Verified';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DataSignalsIcon } from '../global/CustomIcons';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import DataTable from '../common/DataTable';
import { CMButtonOutlined } from '../../containers/styledComponents';

import { fetchRequest as fetchCategories } from '../../redux/actions/marketplace/categories';
import { fetchListings } from '../../redux/actions/marketplace/listings';
import {
  fetchRequest as fetchCategoryCountsRequest,
} from '../../redux/actions/marketplace/categoryCounts';

import {
  KIND_LABELS,
  buildCategoryIndex,
  pricingModelOf, priceSummary, useUsdRate,
  useViewerOrganizations,
  compactRelativeDate, fullDateTime,
} from './marketplaceUtils';
import {
  CATEGORY_GLYPHS, CATEGORY_ACCENTS, OtherGlyph,
} from './CategoryGlyphs';


// ---------------------------------------------------------------------------
// Editorial config: Home tile metadata. Slugs MUST exist in the BE
// seed (categories_seed.py). Accent colors come from the shared
// CATEGORY_ACCENTS map so the listing detail page tints to the same
// hex as the corresponding home tile.
// ---------------------------------------------------------------------------

const MAIN_CATEGORIES = [
  { slug: 'finance',   name: 'Finance',          blurb: 'Stocks, FX, commodities, indices, fixed income.' },
  { slug: 'crypto',    name: 'Crypto',           blurb: 'Spot, derivatives, on-chain, DeFi, NFTs.' },
  { slug: 'macro',     name: 'Macro',            blurb: 'Labor, inflation, GDP, rates.' },
  { slug: 'energy',    name: 'Energy',           blurb: 'Power grid, renewables, oil & gas.' },
  { slug: 'weather',   name: 'Weather',          blurb: 'Temperature, precipitation, wind, air.' },
  { slug: 'climate',   name: 'Climate',          blurb: 'Emissions, sea level, environment.' },
  { slug: 'geo',       name: 'Geo & Earth',      blurb: 'Earthquakes, satellite indices, geospatial.' },
  { slug: 'health',    name: 'Health & Bio',     blurb: 'Wearables, public health, clinical feeds.' },
  { slug: 'robotics',  name: 'Robotics',         blurb: 'IMU, LiDAR, vision, actuator streams.' },
  { slug: 'iot',       name: 'IoT',              blurb: 'Smart home, industrial, sensor networks.' },
  { slug: 'transport', name: 'Transportation',   blurb: 'Traffic, aviation, maritime, mobility.' },
  { slug: 'sports',    name: 'Sports',           blurb: 'Team & player stats, betting markets.' },
  { slug: 'web',       name: 'Web & Social',     blurb: 'Web traffic, search, social, sentiment.' },
  { slug: 'gov',       name: 'Government',       blurb: 'Census, policy, public spending.' },
  { slug: 'other',     name: 'Other',            blurb: 'Everything else from the community.' },
].map((c) => ({ ...c, accent: CATEGORY_ACCENTS[c.slug] || '#6B7280' }));

const MAIN_CATEGORIES_BY_SLUG = MAIN_CATEGORIES.reduce((acc, c) => {
  acc[c.slug] = c; return acc;
}, {});

// Default sort = "Most subscribed first". The BE accepts the friendly
// alias ``subscribers`` and maps it to the denormalized
// ``subscribers_count_cached`` index.
const SORT_OPTIONS = [
  { value: 'subscribers:desc',          label: 'Most subscribed' },
  { value: 'created_at:desc',           label: 'Newest' },
  { value: 'created_at:asc',            label: 'Oldest' },
  { value: 'price_monthly_credits:asc', label: 'Price: low to high' },
  { value: 'price_monthly_credits:desc', label: 'Price: high to low' },
  { value: 'updated_at:desc',           label: 'Recently updated' },
];

const KIND_OPTIONS = [
  { value: '',           label: 'All kinds' },
  { value: 'realtime',   label: 'Real-time' },
  { value: 'historical', label: 'Historical' },
  { value: 'both',       label: 'Real-time + Historical' },
];

const TYPE_OPTIONS = [
  { value: '',       label: 'All types' },
  { value: 'signal', label: 'Signals' },
  { value: 'event',  label: 'Events' },
];

const DEFAULT_FILTERS = {
  kind:          '',
  listing_type:  '',
  verified_only: false,
  featured_only: false,
  free_only:     false,
  sort_by:       'subscribers',
  sort_order:    'desc',
  limit:         25,
  offset:        0,
};


// ---------------------------------------------------------------------------
// Hero / Inline search bar
// ---------------------------------------------------------------------------

const HeroSearch = ({
  value, onChange, onSubmit, placeholder, autoFocus, size = 'large',
}) => {
  const theme = useTheme();
  const big = size === 'large';
  // Both variants are now bare neutral fields - the hero owns the
  // color story so the input stays readable and product-like. The
  // ``big`` variant differs only in scale (taller field, larger
  // glyphs, in-line submit button).
  return (
    <Box
      component="form"
      onSubmit={(e) => { e.preventDefault(); onSubmit(value); }}
      sx={{ width: '100%' }}
    >
      <TextField
        fullWidth
        autoFocus={autoFocus}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{
                fontSize: big ? 24 : 20,
                color: theme.palette.text.secondary,
                ml: big ? 0.5 : 0,
              }} />
            </InputAdornment>
          ),
          endAdornment: big && (
            <InputAdornment position="end">
              <Button
                type="submit"
                variant="contained"
                sx={{
                  borderRadius: 999,
                  px: 3.5, py: 1.1, fontWeight: 700,
                  fontSize: 14,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                Search
              </Button>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 999,
            background: theme.palette.background.paper,
            paddingRight: big ? 0.75 : undefined,
            paddingLeft: big ? 0.5 : undefined,
            '& input': {
              fontSize: big ? 16 : 14,
              padding: big ? '15px 6px' : undefined,
              fontWeight: big ? 500 : 400,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.text.disabled,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.text.primary,
              borderWidth: 1,
            },
          },
        }}
      />
    </Box>
  );
};


// ---------------------------------------------------------------------------
// Home tile — minimal, monochrome. Uses a single accent color that
// only kicks in on hover so the resting state is fully neutral.
// ---------------------------------------------------------------------------

const MainCategoryTile = ({ category, count, onClick }) => {
  const theme = useTheme();
  const Glyph = CATEGORY_GLYPHS[category.slug] || OtherGlyph;
  const accent = category.accent || theme.palette.primary.main;
  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        borderRadius: 2,
        minHeight: 120,
        borderColor: theme.palette.divider,
        // Slight inset on the left so the colored edge doesn't fight
        // the tile's normal border.
        pl: '3px',
        // The 3px accent strip lives in ::before so it doesn't shift
        // the layout and so the resting border can stay neutral.
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, bottom: 0,
          width: 3,
          background: accent,
          opacity: 0.55,
          transition: 'opacity 140ms ease',
        },
        transition: 'border-color 140ms ease, background-color 140ms ease',
        '&:hover': {
          borderColor: alpha(accent, 0.55),
          bgcolor: alpha(accent, 0.03),
        },
        '&:hover::before': { opacity: 1 },
        '&:hover .category-glyph': {
          color: accent,
          borderColor: alpha(accent, 0.4),
          bgcolor: alpha(accent, 0.08),
        },
      }}
    >
      <Box sx={{
        p: 1.75,
        height: '100%',
        display: 'flex', flexDirection: 'column', gap: 1,
      }}>
        <Stack direction="row" alignItems="center" spacing={1.25}
               sx={{ minWidth: 0 }}>
          <Box
            className="category-glyph"
            sx={{
              width: 36, height: 36,
              borderRadius: 1.25,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.text.primary, 0.025),
              color: theme.palette.text.secondary,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
              transition: 'color 140ms ease, border-color 140ms ease, background-color 140ms ease',
            }}
          >
            <Glyph size={20} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={600}
                        sx={{ fontSize: 14, lineHeight: 1.2 }}>
              {category.name}
            </Typography>
            {count > 0 && (
              <Typography variant="caption" color="text.disabled"
                          sx={{ fontSize: 11 }}>
                {count} listing{count === 1 ? '' : 's'}
              </Typography>
            )}
          </Box>
        </Stack>
        <Typography variant="caption" color="text.secondary"
                    sx={{
                      fontSize: 12, lineHeight: 1.4,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}>
          {category.blurb}
        </Typography>
      </Box>
    </Card>
  );
};


// ---------------------------------------------------------------------------
// Sub-category pill — small horizontal box used on the Browse stage.
// Active-state highlights the currently selected sub.
// ---------------------------------------------------------------------------

const SubCategoryPill = ({ name, count, active, onClick }) => {
  const theme = useTheme();
  return (
    <Chip
      onClick={onClick}
      label={
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box component="span" sx={{ fontWeight: active ? 700 : 600 }}>
            {name}
          </Box>
          {count > 0 && (
            <Box
              component="span"
              sx={{
                fontSize: 10,
                fontWeight: 700,
                opacity: 0.7,
                px: 0.5, py: 0.1,
                borderRadius: 999,
                bgcolor: active
                  ? alpha('#fff', 0.25)
                  : alpha(theme.palette.text.primary, 0.08),
              }}
            >
              {count}
            </Box>
          )}
        </Stack>
      }
      variant={active ? 'filled' : 'outlined'}
      color={active ? 'primary' : 'default'}
      sx={{
        height: 32,
        borderRadius: 999,
        cursor: 'pointer',
        fontSize: 12.5,
        '& .MuiChip-label': { px: 1.25 },
        ...(active ? {} : {
          '&:hover': {
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
          },
        }),
      }}
    />
  );
};


// ---------------------------------------------------------------------------
// Listings DataTable — same shared component as the Trainings page.
// ---------------------------------------------------------------------------

const useListingsColumns = (categoryMap) => {
  const theme = useTheme();
  const usdRate = useUsdRate();
  return useMemo(() => [
    {
      key: 'identity',
      label: 'Listing',
      width: 360,
      render: (l) => {
        const seller = l.seller || {};
        const sellerLabel = seller.label || seller.name || 'Anonymous';
        const sellerInitial = sellerLabel.charAt(0).toUpperCase();
        return (
          <Stack direction="row" spacing={1.25} alignItems="center"
                 sx={{ minWidth: 0 }}>
            {l.icon_url ? (
              <Avatar src={l.icon_url} variant="rounded"
                      sx={{ width: 36, height: 36 }} />
            ) : (
              <Avatar variant="rounded"
                      sx={{
                        width: 36, height: 36,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: 'primary.main',
                      }}>
                <DataSignalsIcon fontSize="small" />
              </Avatar>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography fontWeight={700} noWrap sx={{ fontSize: 13.5 }}>
                  {l.title || l.signal_key}
                </Typography>
                {l.featured && (
                  <Tooltip title="Featured">
                    <StarIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                  </Tooltip>
                )}
                {/* The "Free" indicator already lives prominently in
                    the Price column on the right; duplicating it next
                    to the title was visual noise, especially in the
                    dark theme where the green chip read very hot. */}
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Avatar sx={{ width: 14, height: 14, fontSize: 8 }}>
                  {sellerInitial}
                </Avatar>
                <Typography variant="caption" color="text.secondary"
                            noWrap sx={{ fontSize: 11 }}>
                  {sellerLabel}
                </Typography>
                {seller.verified && (
                  <Tooltip title="Verified data provider">
                    <VerifiedIcon sx={{ fontSize: 12, color: 'primary.main' }} />
                  </Tooltip>
                )}
              </Stack>
            </Box>
          </Stack>
        );
      },
    },
    {
      key: 'categories',
      label: 'Categories',
      width: 220,
      render: (l) => {
        const cats = l.categories || [];
        if (cats.length === 0) {
          return (
            <Typography variant="caption" color="text.disabled">—</Typography>
          );
        }
        return (
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            {cats.slice(0, 2).map((c) => (
              <Chip
                key={c}
                label={categoryMap?.[c]?.name || c}
                size="small" variant="outlined"
                sx={{ height: 20, fontSize: 10.5 }}
              />
            ))}
            {cats.length > 2 && (
              <Chip
                label={`+${cats.length - 2}`}
                size="small" variant="outlined"
                sx={{ height: 20, fontSize: 10.5 }}
              />
            )}
          </Stack>
        );
      },
    },
    {
      key: 'kind',
      label: 'Kind',
      width: 130,
      render: (l) => (
        <Typography variant="caption" color="text.secondary">
          {KIND_LABELS[l.kind] || l.kind || '—'}
        </Typography>
      ),
    },
    {
      key: 'subscribers',
      label: 'Subscribers',
      width: 120,
      align: 'right',
      sortable: true,
      render: (l) => (
        <Typography variant="body2" fontWeight={700}
                    sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {(l.subscribers_count || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      key: 'price',
      label: 'Price',
      width: 200,
      align: 'right',
      render: (l) => {
        const model = pricingModelOf(l);
        if (model === 'free') {
          return (
            <Typography fontWeight={700} color="success.main">Free</Typography>
          );
        }
        return (
          <Typography variant="body2" fontWeight={700}
                      sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {priceSummary(l, usdRate)}
          </Typography>
        );
      },
    },
    {
      key: 'updated',
      label: 'Updated',
      width: 90,
      sortable: true,
      render: (l) => {
        const ts = l.updated_at || l.created_at;
        return (
          <Tooltip title={fullDateTime(ts)} arrow placement="top">
            <Typography
              sx={{
                fontSize: 11,
                color: 'text.secondary',
                whiteSpace: 'nowrap',
              }}
            >
              {compactRelativeDate(ts)}
            </Typography>
          </Tooltip>
        );
      },
    },
  ], [categoryMap, theme, usdRate]);
};


// ---------------------------------------------------------------------------
// HOME stage
// ---------------------------------------------------------------------------

const HomeStage = ({
  draftQuery, setDraftQuery, onSubmitSearch, onPickCategory, countsBySlug,
}) => {
  const theme = useTheme();
  return (
    <>
      {/* Hero panel.
          Subtle multi-color gradient lifts the whole top region as a
          single product surface (rather than the search box itself
          carrying the color, which was making the input look like a
          children's toy). The radial blobs are tightly capped so the
          gradient fades into the page background by ~80% across, no
          hard edge. */}
      <Box
        sx={{
          position: 'relative',
          textAlign: 'center',
          py: { xs: 4, md: 6 },
          px: { xs: 2, md: 4 },
          mb: { xs: 3, md: 4 },
          borderRadius: 3,
          overflow: 'hidden',
          // 3-stop diagonal base + 2 radial highlights for depth.
          background: (t) => `
            radial-gradient(60% 80% at 0% 0%,
              ${alpha(t.palette.primary.main, 0.10)} 0%,
              transparent 60%),
            radial-gradient(60% 80% at 100% 0%,
              ${alpha('#A855F7', 0.08)} 0%,
              transparent 60%),
            radial-gradient(70% 100% at 50% 100%,
              ${alpha('#0EA5E9', 0.06)} 0%,
              transparent 60%),
            linear-gradient(135deg,
              ${alpha(t.palette.primary.main, 0.04)} 0%,
              ${alpha('#22C55E', 0.025)} 50%,
              ${alpha('#F59E0B', 0.03)} 100%)`,
          border: (t) => `1px solid ${alpha(t.palette.divider, 0.6)}`,
        }}
      >
        <Stack direction="row" justifyContent="center" alignItems="center"
               spacing={0.75}
               sx={{
                 mb: 1.75,
                 display: 'inline-flex',
                 mx: 'auto',
                 px: 1.25, py: 0.4,
                 borderRadius: 999,
                 bgcolor: (t) => alpha(t.palette.background.paper, 0.7),
                 border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.2)}`,
                 color: 'primary.main',
                 backdropFilter: 'blur(4px)',
               }}>
          <StorefrontIcon sx={{ fontSize: 14 }} />
          <Typography variant="overline"
                      sx={{ fontWeight: 700, letterSpacing: 1.2,
                            fontSize: 10.5, lineHeight: 1 }}>
            Data Signal Marketplace
          </Typography>
        </Stack>
        <Typography variant="h3" fontWeight={700}
                    sx={{ fontSize: { xs: 24, sm: 30, md: 38 }, mb: 1,
                          letterSpacing: -0.5 }}>
          Find the data your workspace needs.
        </Typography>
        <Typography variant="body2" color="text.secondary"
                    sx={{ maxWidth: 600, mx: 'auto', mb: 3.5,
                          fontSize: 13.5 }}>
          Browse thousands of signals across finance, weather, health, IoT and
          more — subscribe and they appear inside your workspace automatically.
        </Typography>
        {/* Wider search field - now the dominant element of the hero. */}
        <Box sx={{ maxWidth: 880, mx: 'auto' }}>
          <HeroSearch
            value={draftQuery}
            onChange={setDraftQuery}
            onSubmit={onSubmitSearch}
            placeholder="Search signals — e.g. 'AAPL price', 'unemployment rate', 'wind speed'"
            autoFocus
          />
        </Box>
      </Box>

      <Stack direction="row" alignItems="baseline" sx={{ mb: 1.5 }}>
        <Typography variant="overline"
                    sx={{ flex: 1, fontWeight: 600, letterSpacing: 0.8,
                          fontSize: 10.5, color: 'text.secondary' }}>
          Browse by category
        </Typography>
        <Typography variant="caption" color="text.disabled"
                    sx={{ fontSize: 11 }}>
          {MAIN_CATEGORIES.length} domains
        </Typography>
      </Stack>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(3, 1fr)',
          md: 'repeat(4, 1fr)',
          lg: 'repeat(5, 1fr)',
          xl: 'repeat(6, 1fr)',
        },
        // Smaller tiles read more like a compact app launcher than a
        // poster wall - tighten the gutters to match.
        gap: 1.25,
      }}>
        {MAIN_CATEGORIES.map((c) => (
          <MainCategoryTile
            key={c.slug}
            category={c}
            count={countsBySlug[c.slug] || 0}
            onClick={() => onPickCategory(c.slug)}
          />
        ))}
      </Box>
    </>
  );
};


// ---------------------------------------------------------------------------
// BROWSE stage — sub-categories on top + DataTable below.
//
// No colored heroes, no gradient strips — once the user clicks into a
// category we go fully neutral so the data is the focus. The same
// inline search box appears on every sub-category page.
// ---------------------------------------------------------------------------

const BrowseStage = ({
  rootSlug, subSlug, query, allCategories,
  draftQuery, setDraftQuery, onSubmitSearch,
  onPickSub,
  filters, setFilters, listingsResponse, isLoading, error,
  onListingClick, countsBySlug,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [filterAnchor, setFilterAnchor] = useState(null);

  const root = MAIN_CATEGORIES_BY_SLUG[rootSlug] || null;
  const { bySlug } = useMemo(
    () => buildCategoryIndex(allCategories || []),
    [allCategories],
  );

  // Sub-categories = direct children of the active root in the BE
  // taxonomy, sorted by ``sort_order``. Rendered as horizontal pills.
  const subs = useMemo(() => {
    if (!rootSlug) return [];
    return (allCategories || [])
      .filter((c) => c.parent_slug === rootSlug)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [allCategories, rootSlug]);

  const sortValue = `${filters.sort_by}:${filters.sort_order}`;
  const handleSortChange = (e) => {
    const [sort_by, sort_order] = e.target.value.split(':');
    setFilters((prev) => ({ ...prev, sort_by, sort_order, offset: 0 }));
  };
  const updateFilter = (patch) =>
    setFilters((prev) => ({ ...prev, ...patch, offset: 0 }));
  const activeAdvancedCount = (filters.verified_only ? 1 : 0)
    + (filters.featured_only ? 1 : 0)
    + (filters.free_only ? 1 : 0);

  // Wire up DataTable's server-driven sort to our filter state for
  // the sortable columns (subscribers, updated).
  const columnToFilterKey = (col) => {
    if (col === 'subscribers') return 'subscribers';
    if (col === 'updated') return 'updated_at';
    return col;
  };
  const filterKeyToColumn = (k) => {
    if (k === 'subscribers' || k === 'subscribers_count_cached') return 'subscribers';
    if (k === 'updated_at') return 'updated';
    return null;
  };
  const handleSortFromColumn = (key, direction) => {
    setFilters((prev) => ({
      ...prev,
      sort_by:    columnToFilterKey(key),
      sort_order: direction,
      offset:     0,
    }));
  };

  const columns = useListingsColumns(bySlug);

  const listings = listingsResponse?.listings || [];
  const total = listingsResponse?.total || 0;

  const headlineTitle = query
    ? `Results for "${query}"`
    : subSlug
      ? (bySlug[subSlug]?.name || subSlug)
      : root
        ? `All ${root.name}`
        : 'All listings';

  return (
    <>
      {/* Plain neutral header — no gradients, no colored chrome. */}
      <Stack direction={{ xs: 'column', md: 'row' }}
             alignItems={{ md: 'center' }}
             justifyContent="space-between"
             spacing={2}
             sx={{ mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" fontWeight={700}>
            {headlineTitle}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {isLoading
              ? 'Loading…'
              : `${total.toLocaleString()} listing${total === 1 ? '' : 's'}`}
          </Typography>
        </Box>
        <Box sx={{ minWidth: { md: 360 }, width: { xs: '100%', md: 'auto' } }}>
          {/* Same compact search bar on every sub-category page. */}
          <HeroSearch
            size="small"
            value={draftQuery}
            onChange={setDraftQuery}
            onSubmit={onSubmitSearch}
            placeholder={
              root
                ? `Search inside ${root.name}…`
                : 'Search listings…'
            }
          />
        </Box>
      </Stack>

      {/* Sub-category pills — small boxes on top, horizontally
          scrollable on narrow widths. The first pill ("All
          [Category]") clears any sub-category drill-down. */}
      {(subs.length > 0 || subSlug) && (
        <Box sx={{
          mb: 2,
          // Horizontal-scroll on narrow screens, wrap on wide ones.
          overflowX: { xs: 'auto', md: 'visible' },
          pb: { xs: 1, md: 0 },
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.text.primary, 0.15),
            borderRadius: 3,
          },
        }}>
          <Stack direction="row" spacing={1} useFlexGap
                 flexWrap={{ xs: 'nowrap', md: 'wrap' }}
                 sx={{ rowGap: 1 }}>
            <SubCategoryPill
              name={root ? `All ${root.name}` : 'All listings'}
              count={countsBySlug[rootSlug] || 0}
              active={!subSlug}
              onClick={() => onPickSub(null)}
            />
            {subs.map((s) => (
              <SubCategoryPill
                key={s.slug}
                name={s.name}
                count={countsBySlug[s.slug] || 0}
                active={subSlug === s.slug}
                onClick={() => onPickSub(s.slug)}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Toolbar (kind / sort / advanced) */}
      <Paper variant="outlined" sx={{ p: 1.25, mb: 2, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}
               flexWrap="wrap" useFlexGap>
          <FormControl size="small">
            <Select
              value={filters.kind}
              onChange={(e) => updateFilter({ kind: e.target.value })}
              displayEmpty
              sx={{ borderRadius: 999, minWidth: 160 }}
            >
              {KIND_OPTIONS.map((k) => (
                <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <Select
              value={filters.listing_type || ''}
              onChange={(e) => updateFilter({ listing_type: e.target.value })}
              displayEmpty
              sx={{ borderRadius: 999, minWidth: 140 }}
            >
              {TYPE_OPTIONS.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <Select
              value={sortValue}
              onChange={handleSortChange}
              sx={{ borderRadius: 999, minWidth: 200 }}
            >
              {SORT_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Quick "Free only" toggle — surfaced in the toolbar so
              buyers don't have to dig into Advanced filters to find
              the no-cost listings. */}
          <Chip
            icon={<LocalOfferIcon sx={{ fontSize: 14 }} />}
            label="Free only"
            clickable
            color={filters.free_only ? 'success' : 'default'}
            variant={filters.free_only ? 'filled' : 'outlined'}
            onClick={() => updateFilter({ free_only: !filters.free_only })}
            sx={{ height: 32, borderRadius: 999, fontWeight: 600 }}
          />
          <Tooltip title="Advanced filters">
            <IconButton
              onClick={(e) => setFilterAnchor(e.currentTarget)}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: activeAdvancedCount > 0
                  ? alpha(theme.palette.primary.main, 0.08)
                  : 'transparent',
              }}
            >
              <Badge badgeContent={activeAdvancedCount} color="primary">
                <FilterAltIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 1, p: 2, width: 280, borderRadius: 2 } }}
      >
        <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
            Advanced filters
          </Typography>
          {activeAdvancedCount > 0 && (
            <Button size="small"
                    startIcon={<FilterAltOffIcon fontSize="small" />}
                    onClick={() => updateFilter({
                      verified_only: false,
                      featured_only: false,
                      free_only:     false,
                    })}>
              Clear
            </Button>
          )}
        </Stack>
        <Divider sx={{ mb: 1.5 }} />
        <Stack spacing={1}>
          <FormControlLabel
            control={
              <Switch checked={filters.verified_only}
                      onChange={(e) => updateFilter({ verified_only: e.target.checked })} />
            }
            label="Verified providers only"
          />
          <FormControlLabel
            control={
              <Switch checked={filters.featured_only}
                      onChange={(e) => updateFilter({ featured_only: e.target.checked })} />
            }
            label="Featured only"
          />
          <FormControlLabel
            control={
              <Switch checked={filters.free_only}
                      onChange={(e) => updateFilter({ free_only: e.target.checked })} />
            }
            label="Free listings only"
          />
        </Stack>
      </Popover>

      {error && (
        <Paper variant="outlined" sx={{
          p: 2, mb: 2,
          borderColor: 'error.main',
          bgcolor: alpha(theme.palette.error.main, 0.04),
        }}>
          <Typography color="error">{String(error)}</Typography>
        </Paper>
      )}

      {/* Listings via shared DataTable (same as Trainings). */}
      <DataTable
        columns={columns}
        rows={listings}
        getRowKey={(l) => l.listing_id}
        loading={isLoading}
        onRowClick={(l) => onListingClick(l.listing_id)}
        sortColumn={filterKeyToColumn(filters.sort_by)}
        sortDirection={filters.sort_order}
        onSortChange={handleSortFromColumn}
        page={Math.floor(filters.offset / filters.limit)}
        rowsPerPage={filters.limit}
        totalItems={total}
        onPageChange={(p) => setFilters((prev) => ({
          ...prev, offset: p * prev.limit,
        }))}
        onRowsPerPageChange={(n) => setFilters((prev) => ({
          ...prev, limit: n, offset: 0,
        }))}
        rowsPerPageOptions={[10, 25, 50, 100]}
        emptyState={{
          icon: <StorefrontIcon />,
          title: 'No listings match your filters',
          message: 'Try clearing the search, switching sub-category, or relaxing the filters above.',
          action: (
            <Button onClick={() => navigate('/marketplace')}>
              Back to marketplace home
            </Button>
          ),
        }}
      />
    </>
  );
};


// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

const MarketplacePage = ({
  fetchCategories, fetchListings, fetchCategoryCounts,
  categoriesResp, listingsResponse, isLoading, error,
  categoryCountsResp,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();

  const root = search.get('cat') || '';
  const sub = search.get('sub') || '';
  const q = search.get('q') || '';
  // Marketplace view scope: ``public`` (default) or ``org``. Persisted
  // in the URL so a shared link to the org-marketplace stays in
  // org-mode after a refresh / share.
  const visParam = (search.get('vis') || 'public').toLowerCase();
  const viewerOrgs = useViewerOrganizations();
  const hasOrg = viewerOrgs.length > 0;
  const visibility = (visParam === 'org' && hasOrg) ? 'org' : 'public';
  const orgUid = visibility === 'org' ? (viewerOrgs[0]?.uid || '') : '';

  // Two stages: home (no cat) vs. browse (cat selected OR active search).
  const stage = (root || q) ? 'browse' : 'home';

  const [draftQuery, setDraftQuery] = useState(q);
  useEffect(() => { setDraftQuery(q); }, [q]);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  // Reset filters when the category context changes so user doesn't
  // carry e.g. "verified only" across categories silently.
  useEffect(() => { setFilters(DEFAULT_FILTERS); }, [root, sub]);

  // Fetch the taxonomy once.
  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  // ----- Listings fetch ----------------------------------------------------
  //
  // ONLY fetch listings on the Browse stage. The previous code pulled
  // up to 100 listings on the Home stage too, just to derive count
  // badges - that downloaded every listing's icon URL, description,
  // tags and pricing for nothing. The Home stage now uses the
  // dedicated ``/category-counts`` endpoint (small Mongo aggregation,
  // single integer per root category) for the badges and never
  // touches the listings table.
  useEffect(() => {
    if (stage !== 'browse') return;
    const params = {
      limit: filters.limit,
      offset: filters.offset,
      sort_by: filters.sort_by,
      sort_order: filters.sort_order,
    };
    // Sub-category narrows further than root, so prefer sub when set.
    if (sub) params.category = sub;
    else if (root) params.category = root;
    if (q) params.q = q;
    if (filters.kind) params.kind = filters.kind;
    if (filters.verified_only) params.verified_only = 'true';
    if (filters.featured_only) params.featured_only = 'true';
    if (filters.free_only) params.free_only = 'true';
    // Org-marketplace scope. The BE resolves the actual org_uid
    // from the calling user's membership; we still send it as a
    // hint so a multi-org user can later pick which org to view.
    if (visibility === 'org') {
      params.visibility = 'org';
      if (orgUid) params.org_uid = orgUid;
    }
    fetchListings(params);
  }, [fetchListings, stage, root, sub, q, filters, visibility, orgUid]);

  // ----- Home-stage category counts ---------------------------------------
  //
  // One cheap call to ``/marketplace/category-counts`` on the Home
  // stage. Replaces the previous "fetch 100 listings on the home
  // page" pattern. Result is just ``{slug: count}`` so we re-fetch
  // it only when the user lands on the home stage (e.g. after
  // clicking back from Browse). Soft-fail: tiles render without
  // badges (the reducer keeps the last good counts on error).
  useEffect(() => {
    if (stage !== 'home') return;
    fetchCategoryCounts({ visibility, orgUid });
  }, [stage, visibility, orgUid, fetchCategoryCounts]);
  const homeCounts = useMemo(
    () => categoryCountsResp || {},
    [categoryCountsResp],
  );

  // Counts shown on category tiles / pills.
  //
  // STRICTLY split by stage so the Home tiles don't get inflated by
  // a stale Browse-stage ``listingsResponse`` sitting in Redux:
  //
  //   * Home    - ONLY the authoritative BE counts. Never folds in
  //               the listings cache. (The previous "merge" version
  //               grew the root count every time the user
  //               visited a category and came back, because every
  //               listing in the cached response that happened to
  //               match a root slug bumped the tile.)
  //   * Browse  - derived from the current page's listings so the
  //               sub-category pills can show approximate counts
  //               without a second BE call. Root pills ("All
  //               <root>") still pull from the home-counts map so
  //               they match what the user just clicked.
  const countsBySlug = useMemo(() => {
    if (stage !== 'browse') {
      // Home: nothing but the BE counts. No bubble-up, no merge.
      return { ...homeCounts };
    }
    const out = { ...homeCounts };
    (listingsResponse?.listings || []).forEach((l) => {
      (l.categories || []).forEach((c) => {
        // Only count the LEAF slug for sub-category pills - never
        // bump the root from listings, the home-counts map is the
        // source of truth for that.
        if (homeCounts[c] === undefined) {
          out[c] = (out[c] || 0) + 1;
        }
      });
    });
    return out;
  }, [stage, listingsResponse, homeCounts]);

  const allCategories = categoriesResp?.categories || [];

  // ----- Navigation helpers ------------------------------------------------
  // Every helper preserves the active ``vis`` scope so the user
  // doesn't get bounced back to the public marketplace whenever
  // they click into a category, sub-category, search or "Home"
  // while browsing the org marketplace.
  const withScope = (params) => {
    const out = { ...params };
    if (visibility === 'org') out.vis = 'org';
    return out;
  };
  const goHome = () => setSearch(withScope({}));
  const goCategory = (slug) => setSearch(withScope({ cat: slug }));
  const goSub = (slug) => {
    const next = {};
    if (root) next.cat = root;
    if (slug) next.sub = slug;
    if (q) next.q = q;
    setSearch(withScope(next));
  };
  const goSearch = (text) => {
    const next = {};
    const t = (text || '').trim();
    if (t) next.q = t;
    if (root) next.cat = root;
    if (sub) next.sub = sub;
    setSearch(withScope(next));
  };

  const breadcrumbItems = useMemo(() => {
    const items = [
      { text: 'Data Signals', link: '/signals', icon: <DataSignalsIcon /> },
      { text: 'Marketplace', link: '/marketplace' },
    ];
    if (root) items.push({
      text: MAIN_CATEGORIES_BY_SLUG[root]?.name || root,
      link: `/marketplace?cat=${root}`,
    });
    if (sub) {
      const bySlug = (allCategories || []).reduce((a, c) => {
        a[c.slug] = c; return a;
      }, {});
      items.push({ text: bySlug[sub]?.name || sub });
    } else if (q) {
      items.push({ text: `Search · "${q}"` });
    }
    return items;
  }, [root, sub, q, allCategories]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', md: 'row' }}
             alignItems={{ md: 'center' }}
             justifyContent="space-between"
             spacing={1}
             sx={{ mb: 2 }}>
        <PageBreadcrumbs items={breadcrumbItems} />
        <Stack direction="row" spacing={1.25} alignItems="center">
          {/* Public / Organization scope toggle. Only rendered for
              users who actually belong to an organization - everyone
              else just sees the public marketplace. Org listings are
              free for org members and never appear in public mode. */}
          {hasOrg && (
            <Paper
              variant="outlined"
              sx={{
                display: 'inline-flex',
                borderRadius: 999,
                p: 0.25,
                bgcolor: theme.palette.background.paper,
              }}
            >
              {[
                { value: 'public', label: 'Public' },
                { value: 'org',    label: viewerOrgs[0]?.name || 'Organization' },
              ].map((opt) => {
                const active = visibility === opt.value;
                return (
                  <Box
                    key={opt.value}
                    onClick={() => {
                      const next = new URLSearchParams(search);
                      if (opt.value === 'public') next.delete('vis');
                      else next.set('vis', 'org');
                      // Reset category context when switching scopes -
                      // the categories on display don't follow you.
                      next.delete('cat');
                      next.delete('sub');
                      next.delete('q');
                      setSearch(next);
                    }}
                    sx={{
                      px: 1.5, py: 0.5,
                      borderRadius: 999,
                      fontSize: 12, fontWeight: 600,
                      cursor: 'pointer',
                      // Use the theme's automatic ``contrastText`` so
                      // the active label stays readable on whatever
                      // ``primary.main`` resolves to (the green
                      // brand color is light enough that white on
                      // green was illegible in dark mode).
                      color: active
                        ? (theme.palette.primary.contrastText || theme.palette.getContrastText(theme.palette.primary.main))
                        : 'text.secondary',
                      bgcolor: active ? 'primary.main' : 'transparent',
                      transition: 'background-color 120ms ease, color 120ms ease',
                      '&:hover': active ? {} : {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    {opt.label}
                  </Box>
                );
              })}
            </Paper>
          )}
          {stage !== 'home' && (
            <CMButtonOutlined
              startIcon={<ArrowBackIcon />}
              onClick={goHome}
            >
              Marketplace home
            </CMButtonOutlined>
          )}
        </Stack>
      </Stack>

      {stage === 'home' ? (
        <HomeStage
          draftQuery={draftQuery}
          setDraftQuery={setDraftQuery}
          onSubmitSearch={goSearch}
          onPickCategory={goCategory}
          countsBySlug={countsBySlug}
        />
      ) : (
        <BrowseStage
          rootSlug={root}
          subSlug={sub}
          query={q}
          allCategories={allCategories}
          draftQuery={draftQuery}
          setDraftQuery={setDraftQuery}
          onSubmitSearch={goSearch}
          onPickSub={goSub}
          filters={filters}
          setFilters={setFilters}
          listingsResponse={listingsResponse}
          isLoading={isLoading}
          error={error}
          onListingClick={(id) => navigate(`/marketplace/listings/${id}`)}
          countsBySlug={countsBySlug}
        />
      )}
    </Box>
  );
};

const mapStateToProps = (state) => ({
  categoriesResp:     state.marketplace_categories.response,
  listingsResponse:   state.marketplace_list.response,
  isLoading:          state.marketplace_list.isLoading,
  error:              state.marketplace_list.error,
  categoryCountsResp: state.marketplace_category_counts.response,
});

const mapDispatchToProps = (dispatch) => ({
  fetchCategories:     () => dispatch(fetchCategories()),
  fetchListings:       (filters) => dispatch(fetchListings(filters)),
  fetchCategoryCounts: (params) => dispatch(fetchCategoryCountsRequest(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MarketplacePage);
